import api from './api';
import type { AuthResponse, User } from '@/types';

class AuthService {
  /**
   * Inicia el flujo de autenticación con Microsoft
   * Redirige al usuario a Microsoft Login
   */
  async login(companyId?: string): Promise<void> {
    const response = await api.get<{ authUrl: string }>('/auth/login', companyId ? { companyId } : undefined);
    
    if (response.success && response.data?.authUrl) {
      // Redirigir a Microsoft para autenticación
      window.location.href = response.data.authUrl;
    } else {
      throw new Error('Failed to get authorization URL');
    }
  }

  /**
   * Inicia el flujo de autenticación con Google
   * Redirige al usuario a Google Login
   */
  async loginGoogle(companyId?: string): Promise<void> {
    const response = await api.get<{ authUrl: string }>('/auth/login/google', companyId ? { companyId } : undefined);
    
    if (response.success && response.data?.authUrl) {
      // Redirigir a Google para autenticación
      window.location.href = response.data.authUrl;
    } else {
      throw new Error('Failed to get Google authorization URL');
    }
  }

  /**
   * Maneja el callback de Microsoft OAuth
   * Se llama cuando Microsoft redirige de vuelta con el código
   */
  async handleCallback(code: string): Promise<AuthResponse | null> {
    const response = await api.get<AuthResponse>(`/auth/callback?code=${code}`);
    
    if (response.success && response.data) {
      this.saveTokens(response.data);
      return response.data;
    }
    
    return null;
  }

  async logout(): Promise<void> {
    await api.post('/auth/logout');
    this.clearTokens();
  }

  async getCurrentUser(): Promise<User | null> {
    const response = await api.get<User>('/auth/me');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    return null;
  }

  /**
   * Refresca la sesión interna del buzón.
   * `permanent: true` = refresh token inválido/expirado (hay que reconectar);
   * `permanent: false` = fallo transitorio (red/5xx), los tokens siguen siendo válidos.
   */
  async refreshSession(): Promise<{ ok: true; accessToken: string } | { ok: false; permanent: boolean }> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return { ok: false, permanent: true };
    }

    const response = await api.post<{ accessToken: string; refreshToken: string; expiresIn?: number }>('/auth/refresh', {
      refreshToken,
    });

    if (response.success && response.data?.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      if (response.data.expiresIn) {
        localStorage.setItem('expiresAt', String(Date.now() + response.data.expiresIn * 1000));
      }
      // Mantener el mapa de sesiones por compañía sincronizado con la sesión activa
      const companyId = localStorage.getItem('emailCompanyId');
      if (companyId) {
        this.saveCurrentSessionToMap(companyId);
      }
      return { ok: true, accessToken: response.data.accessToken };
    }

    const status = response.error?.statusCode ?? 0;
    return { ok: false, permanent: status >= 400 && status < 500 };
  }

  saveTokens(authData: Partial<AuthResponse> & Record<string, any>): void {
    const expiresAt = authData.expiresIn ? String(Date.now() + authData.expiresIn * 1000) : null;

    // Claves planas (sesión activa actual)
    if (authData.accessToken) localStorage.setItem('accessToken', authData.accessToken);
    if (authData.refreshToken) localStorage.setItem('refreshToken', authData.refreshToken);
    if (expiresAt) localStorage.setItem('expiresAt', expiresAt);
    if (authData.companyId) localStorage.setItem('emailCompanyId', authData.companyId);
    if (authData.provider) localStorage.setItem('emailProvider', authData.provider);
    if (authData.user) localStorage.setItem('user', JSON.stringify(authData.user));

    // Guardar también en el mapa de sesiones por compañía
    if (authData.companyId) {
      const sessions = this.getAllSessions();
      sessions[authData.companyId] = {
        accessToken: authData.accessToken || '',
        refreshToken: authData.refreshToken || '',
        expiresAt: expiresAt || '',
        provider: authData.provider || '',
        user: authData.user || null,
      };
      localStorage.setItem('emailSessions', JSON.stringify(sessions));
    }
  }

  getAllSessions(): Record<string, any> {
    try {
      const raw = localStorage.getItem('emailSessions');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  getSessionForCompany(companyId: string): Record<string, any> | null {
    const sessions = this.getAllSessions();
    return sessions[companyId] || null;
  }

  hasSessionForCompany(companyId: string): boolean {
    const session = this.getSessionForCompany(companyId);
    return !!(session?.accessToken);
  }

  loadSessionForCompany(companyId: string): boolean {
    const session = this.getSessionForCompany(companyId);
    if (!session?.accessToken) return false;

    localStorage.setItem('accessToken', session.accessToken);
    if (session.refreshToken) localStorage.setItem('refreshToken', session.refreshToken);
    if (session.expiresAt) localStorage.setItem('expiresAt', session.expiresAt);
    if (session.provider) localStorage.setItem('emailProvider', session.provider);
    localStorage.setItem('emailCompanyId', companyId);
    if (session.user) localStorage.setItem('user', JSON.stringify(session.user));
    return true;
  }

  saveCurrentSessionToMap(companyId: string): void {
    const sessions = this.getAllSessions();
    sessions[companyId] = {
      accessToken: localStorage.getItem('accessToken') || '',
      refreshToken: localStorage.getItem('refreshToken') || '',
      expiresAt: localStorage.getItem('expiresAt') || '',
      provider: localStorage.getItem('emailProvider') || '',
      user: (() => { try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; } })(),
    };
    localStorage.setItem('emailSessions', JSON.stringify(sessions));
  }

  clearTokens(companyId?: string): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('emailProvider');
    localStorage.removeItem('emailCompanyId');
    localStorage.removeItem('user');

    // Si se provee companyId, eliminar también del mapa de sesiones
    if (companyId) {
      const sessions = this.getAllSessions();
      delete sessions[companyId];
      localStorage.setItem('emailSessions', JSON.stringify(sessions));
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    const expiresAt = localStorage.getItem('expiresAt');

    if (!token || !expiresAt) {
      return false;
    }

    return Date.now() < parseInt(expiresAt);
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
}

export default new AuthService();
